// Vendor imports
const React = require('react')
const qs = require('qs')
// Shared imports
const BemEntity = require('shared/bem')
const { urlWithoutProtocol } = require('shared/utils')
// Client V2 common imports
const useToggleState = require('client-v2/common/hooks/toggle-state')
const { CapsuleButton, CircleButton } = require('client-v2/common/components/buttons')
const CirclePositioner = require('client-v2/common/components/circle-positioner')
const EmbeddedPlayer = require('client-v2/common/components/embedded-player')
const LazyImage = require('client-v2/common/components/lazy-image')
const Modal = require('client-v2/common/components/modal')
const Portal = require('client-v2/common/components/portal')
const Icon = require('client-v2/common/containers/icons/component')
const tag = require('client-v2/common/utils/hyperscript')
// Entry imports
const IntoHeader = require('client-v2/into-landing/containers/header/component-smart')
const IntoLinkModal = require('client-v2/into-landing/containers/link-modal/component-smart')

const bem = BemEntity('IntoContentProfileLayout')

function IntoContentProfileLayout(props) {
  const {
    // Flags
    isShareReferral,
    // Values
    iconId,
    likers,
    provider: { faviconURL, id, streamTitle },
    referredBy,
    related,
    url,
    // Callbacks
    onClick
  } = props
  const streamId = id === streamTitle.toLowerCase() ? streamTitle : null
  const [ isLikerModalVisible, toggleLikerModalVisible ] = useToggleState(false)

  return (
    tag('div', {
      className: bem.block({
        video: !!props.embedUrl
      })
    },
      tag('div', {
        className: bem.element('sidebar')
      },
        tag(IntoHeader, {
          className: bem.element('header')
        }),
        tag('div', {
          className: bem.element('details')
        },
          referredBy && tag('div', {
            className: bem.element('sharer')
          },
            tag(IntoUserAvatar, {
              iconId,
              user: referredBy,
              onClick
            }),
            tag('div', {
              className: bem.element('sharer-details')
            },
              tag('div', {
                className: bem.element('sharer-name')
              },
                referredBy['display_name']
              ),
              tag('div', {
                className: bem.element('sharer-caption')
              },
              isShareReferral && 'shared this with you'
              )
            )
          ),
          tag('div', {
            className: bem.element('title')
          },
            props.title
          ),
          tag('div', {
            className: bem.element('provider')
          },
            tag('a', {
              className: bem.element('provider-info'),
              href: url,
              rel: 'external nofollow noopener',
              target: '_blank'
            },
              tag('div', {
                className: bem.element('source')
              },
                streamId || id
              ),
              (!streamId) && tag('div', {
                className: bem.element('stream')
              },
                streamTitle
              )
            ),
            tag('a', {
              className: bem.element('provider-icon-wrapper'),
              href: url,
              rel: 'external nofollow noopener',
              target: '_blank'
            },
              tag(LazyImage, {
                className: bem.element('provider-icon'),
                rel: 'external nofollow noopener',
                target: '_blank',
                sources: [ faviconURL ]
              })
            )
          ),
          related.size > 0 && tag([
            tag('div', {
              className: bem.element('subheadline')
            },
              'Related topics'
            ),
            tag('div', {
              className: bem.element('related')
            },
              related.map(item => (
                tag('a', {
                  className: bem.element('related-item'),
                  href: `/topic/${item.slug}`
                },
                  tag('span', {
                    className: bem.element('related-hashtag')
                  },
                    ' # '
                  ),
                  item.name
                )
              ))
            )
          ]),
          (likers && likers.length > 1) && tag([
            tag('div', {
              className: bem.element('subheadline')
            },
              'People who liked this'
            ),
            tag('div', {
              className: bem.element('likers')
            },
              likers.slice(0, 4).map(liker => (
                tag(IntoLiker, {
                  key: liker.user_id,
                  user: liker,
                  ...props
                })
              ))
            ),
            (likers.size > 4) && tag('button', {
              className: bem.element('see-more'),
              onClick: toggleLikerModalVisible
            },
              `+ ${likers.size - 4} more`
            )
          ])
        ),
        tag(IntoLinkModal, {
          className: bem.element('link-modal')
        })
      ),
      tag('div', {
        className: bem.element('content-wrapper')
      },
        tag(IntoCardContent, props)
      ),
      Portal.toModalRoot(
        tag(LikersModal, {
          isVisible: isLikerModalVisible,
          onDismiss: toggleLikerModalVisible,
          likers,
          ...props
        })
      )
    )
  )
}

module.exports = IntoContentProfileLayout

function IntoLiker(props) {
  const { baseUrl, iosDeeplinkHref, isAndroid, isDesktop, isIos, onFollowClick, user } = props

  const handleFollowClick = React.useCallback(
    event => {
      if (isAndroid) {
        return
      }
      event.preventDefault()
      const userProfile = baseUrl + `/${props.user.username}`
      const deeplinkData = {
        // Delivered as query parameter when handling the universal links.
        'deeplink_url': userProfile,
        // Fallbacks to mix://$deeplink_path when universal links do not work.
        // Docs: https://docs.branch.io/pages/links/integrate/#deep-linking
        '$deeplink_path': urlWithoutProtocol(userProfile)
      }
      const queryString = qs.stringify(deeplinkData)
      if (isIos) {
        location.assign(`${iosDeeplinkHref}?${queryString}`)
        return
      }
      if (isDesktop){
        onFollowClick({
          deeplinkData,
          isDownloadPromptVisible: true
        })
      }
    },
    [ baseUrl, iosDeeplinkHref, isAndroid, isDesktop, isIos, onFollowClick, user ]
  )

  return tag('div', {
    className: bem.element('user-wrapper')
  },
    tag('div', {
      className: bem.element('user')
    },
      tag(IntoUserAvatar, {
        ...props,
        iconId: ''
      }),
      tag('a', {
        className: bem.element('user-info'),
        href: `/${props.user.username}`
      },
        tag('span', {
          className: bem.element('user-name')
        },
          props.user['display_name']
        ),
        tag('span', {
          className: bem.element('user-likes')
        },
          props.user.likeText
        )
      )
    ),
    tag(CapsuleButton, {
      className: bem.element('follow'),
      onClick: handleFollowClick
    },
      'Follow'
    )
  )
}

function IntoUserAvatar(props) {
  const { iconId, user, onClick } = props
    // Temporary workaround while we have multiple paths for profile navigation
  return (
    tag('a', {
      className: bem.element('user-avatar'),
      href: `/${user.username}`,
      onClick: onClick
    },
      tag(LazyImage, {
        className: bem.element('user-image'),
        rel: 'external nofollow noopener',
        target: '_blank',
        sources: [ user['photo_url'] ]
      }),
      tag(CirclePositioner, {
        className: bem.element('badge-positioner')
      },
        tag(Icon, {
          className: bem.element('badge'),
          id: iconId
        })
      )
    )
  )
}

function IntoCardContent(props) {
  const { isRedditVideo, embedUrl, thumbnail, url } = props
  if (!embedUrl) {
    if (isRedditVideo) {
      return tag('a', {
        className: bem.element('reddit-video'),
        href: url,
        rel: 'external nofollow noopener',
        target: '_blank'
      },
        tag('img', {
          className: bem.element('image'),
          src: thumbnail
        })
      )
    }
    return tag('img', {
      className: bem.element('image'),
      src: thumbnail
    })
  }
  return (
    tag('div', {
      className: bem.element('embed-wrapper')
    },
      tag(LazyImage, {
        className: bem.element('embed-image'),
        rel: 'external nofollow noopener',
        target: '_blank',
        sources: [ thumbnail ]
      }),
      tag(EmbeddedPlayer, {
        className: bem.element('embed-player'),
        src: embedUrl,
        fallbackUrl: url
      })
    )
  )
}

function LikersModal(props) {
  const { isVisible, onDismiss, likers } = props
  if (!isVisible) {
    return null
  }
  return (
    tag(Modal, {
      className: bem.element('liker-modal'),
      onDismiss
    },
      tag('div', {
        className: bem.element('close-button-wrapper')
      },
        tag('div', {
          className: bem.element('modal-header')
        },
          'People who liked this'
        ),
        tag(CircleButton, {
          className: bem.element('close-button'),
          onClick: props.onDismiss
        },
          tag(Icon, { id: 'x' })
        )
      ),
      likers.map(liker => (
        tag(IntoLiker, {
          key: liker.user_id,
          user: liker,
          ...props
        })
      ))
    )
  )
}
